import React, { forwardRef, useState } from "react";

import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { cva, cx, VariantProps } from "class-variance-authority";

import { alertStyles } from "./Alert.css";

export const alertStyle = cva(alertStyles.base, {
    variants: {
        type: {
            info: alertStyles.types.info,
            success: alertStyles.types.success,
            warning: alertStyles.types.warning,
            error: alertStyles.types.error,
        },
        banner: {
            true: alertStyles.banner,
            false: "",
        },
        withDescription: {
            true: alertStyles.withDescription,
            false: "",
        },
    },
});

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
    type?: VariantProps<typeof alertStyle>["type"];
    message?: React.ReactNode;
    description?: React.ReactNode;
    showIcon?: boolean;
    icon?: React.ReactNode;
    closable?: boolean;
    onClose?: React.MouseEventHandler<HTMLButtonElement>;
    banner?: boolean;
    action?: React.ReactNode;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
    (
        {
            className,
            type = "info",
            message,
            description,
            showIcon = false,
            icon,
            closable = false,
            onClose,
            banner = false,
            action,
            ...props
        },
        ref
    ) => {
        const [closed, setClosed] = useState(false);
        if (closed) {
            return null;
        }

        const hasDescription = Boolean(description);
        // A custom icon implies showing one — no need to also pass showIcon.
        const withIcon = showIcon || Boolean(icon);
        const displayIcon = icon ?? <WarningOutlined />;
        const iconClassName = cx(
            alertStyles.icon,
            hasDescription && alertStyles.iconWithDescription
        );
        const titleClassName = cx(
            alertStyles.title,
            hasDescription && alertStyles.titleWithDescription
        );

        return (
            <div
                ref={ref}
                role="alert"
                className={alertStyle({
                    type,
                    banner,
                    withDescription: hasDescription,
                    className,
                })}
                {...props}
            >
                {withIcon && (
                    <span className={iconClassName}>{displayIcon}</span>
                )}
                <div className={alertStyles.content}>
                    {message && <div className={titleClassName}>{message}</div>}
                    {description && (
                        <div className={alertStyles.description}>
                            {description}
                        </div>
                    )}
                </div>
                {(action || closable) && (
                    <div className={alertStyles.actions}>
                        {action}
                        {closable && (
                            <button
                                type="button"
                                aria-label="Close"
                                className={alertStyles.closeButton}
                                onClick={(e) => {
                                    setClosed(true);
                                    onClose?.(e);
                                }}
                            >
                                <CloseOutlined />
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }
) as React.ForwardRefExoticComponent<
    AlertProps & React.RefAttributes<HTMLDivElement>
> & {
    displayName?: string;
};

Alert.displayName = "Alert";
